import React, { useState, useEffect } from 'react';
import { Plus, Edit, Search, Download, X, Eye } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function JefaturaCursos() {
    const [cursos, setCursos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [errors, setErrors] = useState({});
    const [showModulosModal, setShowModulosModal] = useState(false);
    const [selectedCurso, setSelectedCurso] = useState(null);

    // Timezone-safe date formatter: extracts YYYY-MM-DD to avoid UTC shift
    const formatSafeDate = (dateStr) => {
        if (!dateStr) return '-';
        const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            const d = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
            return d.toLocaleDateString();
        }
        return new Date(dateStr).toLocaleDateString();
    };

    const emptyModulo = {
        nombre: '',
        facilitador: '',
        fecha_inicio: '',
        fecha_fin: '',
        hora_inicio: '',
        hora_fin: '',
        dias: { lunes: false, martes: false, miercoles: false, jueves: false, viernes: false },
        duracion: '',
        carga_horaria: '',
        sesiones: '',
        sala: ''
    };

    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        costo: '',
        numero_cuotas: '',
        observaciones: '',
        capacidad: '',
        modulos: [{ ...emptyModulo }]
    });

    useEffect(() => {
        loadCursos();
    }, []);

    const loadCursos = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/jefatura/cursos`);
            const data = await response.json();
            setCursos(data.cursos || []);
        } catch (error) {
            console.error('Error:', error);
            setCursos([]);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleModuloChange = (index, field, value) => {
        setFormData(prev => {
            const newModulos = [...prev.modulos];
            if (field.startsWith('dia_')) {
                const dia = field.replace('dia_', '');
                newModulos[index] = {
                    ...newModulos[index],
                    dias: { ...newModulos[index].dias, [dia]: value }
                };
            } else {
                newModulos[index] = { ...newModulos[index], [field]: value };
            }
            return { ...prev, modulos: newModulos };
        });
    };

    const addModulo = () => {
        setFormData(prev => ({
            ...prev,
            modulos: [...prev.modulos, { ...emptyModulo }]
        }));
    };

    const removeModulo = (index) => {
        if (formData.modulos.length > 1) {
            setFormData(prev => ({
                ...prev,
                modulos: prev.modulos.filter((_, i) => i !== index)
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.nombre.trim()) newErrors.nombre = 'Nombre es requerido';
        if (!formData.costo) newErrors.costo = 'Costo es requerido';
        if (!formData.numero_cuotas) newErrors.numero_cuotas = 'Número de cuotas es requerido';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const resetForm = () => {
        setFormData({
            nombre: '',
            descripcion: '',
            costo: '',
            numero_cuotas: '',
            observaciones: '',
            capacidad: '',
            modulos: [{ ...emptyModulo }]
        });
        setEditingId(null);
    };

    const handleSubmit = async () => {
        console.log('handleSubmit called, formData:', formData);
        const isValid = validateForm();
        console.log('Validation result:', isValid, 'Errors:', errors);
        if (!isValid) {
            console.log('Validation failed, errors:', errors);
            return;
        }

        try {
            const url = editingId
                ? `${API_URL}/jefatura/cursos/${editingId}`
                : `${API_URL}/jefatura/cursos`;

            // Convert module dias to strings
            const modulosFormatted = formData.modulos.map(modulo => ({
                ...modulo,
                dias: Object.entries(modulo.dias)
                    .filter(([_, selected]) => selected)
                    .map(([dia, _]) => dia)
                    .join(',')
            }));

            const dataToSend = {
                ...formData,
                modulos: modulosFormatted
            };

            console.log('=== SENDING REQUEST ===');
            console.log('URL:', url);
            console.log('Method:', editingId ? 'PUT' : 'POST');
            console.log('Data:', dataToSend);

            const response = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(dataToSend)
            });

            console.log('Response status:', response.status);
            console.log('Response ok:', response.ok);

            if (response.ok) {
                const result = await response.json();
                console.log('Success result:', result);
                setShowModal(false);
                setEditingId(null);
                resetForm();
                loadCursos();
                alert('Curso guardado exitosamente!');
            } else {
                const error = await response.json();
                console.log('Error response:', error);
                alert(error.error || 'Error al guardar curso');
            }
        } catch (error) {
            console.error('Fetch Error:', error);
            alert('Error al guardar curso: ' + error.message);
        }
    };

    const exportToExcel = () => {
        try {
            if (!filteredCursos || filteredCursos.length === 0) {
                alert('No hay datos para exportar');
                return;
            }

            const rows = [];
            let counter = 1;

            filteredCursos.forEach((c) => {
                // Parse modulos
                let modulos = c.modulos;
                if (typeof modulos === 'string') {
                    try { modulos = JSON.parse(modulos); } catch (e) { modulos = []; }
                }
                if (!Array.isArray(modulos)) modulos = [];

                const courseBaseInfo = {
                    'No': counter++,
                    'Nombre Curso': c.nombre || '',
                    'Descripción': c.descripcion || '',
                    'Costo': c.costo || '',
                    'Cuotas': c.numero_cuotas || '',
                    'Fecha Límite': formatSafeDate(c.fecha_limite_inscripcion),
                    'Estado': getEstadoLabel(c.estado)
                };

                if (modulos.length === 0) {
                    rows.push({
                        ...courseBaseInfo,
                        'Módulo': '-',
                        'Facilitador': '-',
                        'Fecha Inicio': '-',
                        'Fecha Fin': '-',
                        'Hora Inicio': '-',
                        'Hora Fin': '-',
                        'Días': '-',
                        'Duración': '-',
                        'Carga Horaria': '-',
                        'Sesiones': '-',
                        'Sala': '-'
                    });
                } else {
                    modulos.forEach((m, idx) => {
                        rows.push({
                            ...courseBaseInfo,
                            'Módulo': m.nombre || `Módulo ${idx + 1}`,
                            'Facilitador': m.facilitador || '-',
                            'Fecha Inicio': formatSafeDate(m.fecha_inicio),
                            'Fecha Fin': formatSafeDate(m.fecha_fin),
                            'Hora Inicio': m.hora_inicio || '-',
                            'Hora Fin': m.hora_fin || '-',
                            'Días': m.dias || '-',
                            'Duración': m.duracion || '-',
                            'Carga Horaria': m.carga_horaria || '-',
                            'Sesiones': m.sesiones || '-',
                            'Sala': m.sala || '-'
                        });
                    });
                }
            });

            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Cursos');
            XLSX.writeFile(wb, `Cursos_Extension_${new Date().getFullYear()}.xlsx`);
        } catch (error) {
            console.error('Error al exportar Excel:', error);
            alert('Error al generar el archivo Excel.');
        }
    };

    const handleEdit = (curso) => {
        setEditingId(curso.id_curso);

        // Parse modulos if they exist
        let modulosParsed = [{ ...emptyModulo }];
        if (curso.modulos && Array.isArray(curso.modulos) && curso.modulos.length > 0) {
            modulosParsed = curso.modulos.map(m => {
                let diasObj = { lunes: false, martes: false, miercoles: false, jueves: false, viernes: false };
                if (m.dias && typeof m.dias === 'string') {
                    m.dias.split(',').forEach(d => {
                        if (diasObj.hasOwnProperty(d.trim())) {
                            diasObj[d.trim()] = true;
                        }
                    });
                }
                return {
                    nombre: m.nombre || '',
                    facilitador: m.facilitador || '',
                    fecha_inicio: m.fecha_inicio ? new Date(m.fecha_inicio).toISOString().split('T')[0] : '',
                    fecha_fin: m.fecha_fin ? new Date(m.fecha_fin).toISOString().split('T')[0] : '',
                    hora_inicio: m.hora_inicio || '',
                    hora_fin: m.hora_fin || '',
                    dias: diasObj,
                    duracion: m.duracion || '',
                    carga_horaria: m.carga_horaria || '',
                    sesiones: m.sesiones || '',
                    sala: m.sala || ''
                };
            });
        }

        setFormData({
            nombre: curso.nombre || '',
            descripcion: curso.descripcion || curso.modalidad || '',
            costo: curso.costo || '',
            numero_cuotas: curso.numero_cuotas || '',
            observaciones: curso.observaciones || '',
            capacidad: curso.capacidad || '',
            modulos: modulosParsed
        });
        setShowModal(true);
    };

    const getEstadoColor = (estado) => {
        switch (estado) {
            case 'pendiente_aprobacion': return 'bg-yellow-200 text-yellow-800';
            case 'aprobado': return 'bg-green-200 text-green-800';
            case 'disponible': return 'bg-green-200 text-green-800';
            case 'rechazado': return 'bg-red-200 text-red-800';
            default: return 'bg-gray-200 text-gray-700';
        }
    };

    const getEstadoLabel = (estado) => {
        switch (estado) {
            case 'pendiente_aprobacion': return 'Pendiente Aprobación';
            case 'aprobado': return 'Aprobado';
            case 'disponible': return 'Aprobado';
            case 'rechazado': return 'Rechazado';
            default: return estado || 'Pendiente';
        }
    };

    const handleViewModulos = (curso) => {
        let parsedModulos = curso.modulos;
        if (typeof parsedModulos === 'string') {
            try { parsedModulos = JSON.parse(parsedModulos); } catch (e) { parsedModulos = []; }
        }
        setSelectedCurso({ ...curso, modulos: parsedModulos });
        setShowModulosModal(true);
    };

    // Format days as abbreviations
    const formatDias = (dias) => {
        if (!dias) return '-';
        const diasMap = {
            'lunes': 'L',
            'martes': 'M',
            'miercoles': 'Mi',
            'jueves': 'J',
            'viernes': 'V'
        };
        return dias.split(',').map(d => diasMap[d.trim()] || d.trim()).join(', ');
    };

    const filteredCursos = cursos.filter(c =>
        searchTerm === '' || c.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="p-6 text-center">Cargando...</div>;

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">Jefatura de Carrera &gt; Cursos de Extensión</h1>
            </div>

            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-4 items-end">

                    <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">GESTIÓN</label>
                        <select
                            value={new Date().getFullYear()}
                            className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                        >
                            {[2024, 2025, 2026, 2027].map(g => (
                                <option key={g} value={g}>Gestión {g}</option>
                            ))}
                        </select>
                        <span className="absolute right-3 top-[30px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>

                    <div className="flex-grow flex items-end gap-2">
                        <div className="relative flex-grow">
                            <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Buscar..."
                                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                />
                            </div>
                        </div>
                        <button onClick={() => setShowModal(true)}
                            className="flex items-center gap-2 px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800">
                            <Plus size={20} /> Añadir Curso
                        </button>
                        <button onClick={exportToExcel} className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm" title="Descargar Excel">
                            <Download size={20} />
                        </button>

                    </div>
                </div>
            </div>

            {/* <div className="mb-4 flex flex-wrap gap-3">
                <select
                    value={new Date().getFullYear()}
                    className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm"
                >
                    {[2024, 2025, 2026, 2027].map(g => (
                        <option key={g} value={g}>Gestión {g}</option>
                    ))}
                </select>
                <button onClick={() => setShowModal(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-full hover:bg-blue-700">
                    <Plus size={20} /> Añadir Curso
                </button>
                <div className="flex-1 max-w-md relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full" />
                </div>
                <button onClick={exportToExcel} className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm" title="Descargar Excel">
                    <Download size={20} />
                </button>
            </div> */}

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-3 text-center font-semibold border">No</th>
                                <th className="px-3 py-3 text-center font-semibold border">Nombre Curso</th>
                                <th className="px-3 py-3 text-center font-semibold border">Descripción Curso</th>
                                <th className="px-3 py-3 text-center font-semibold border">Precio Curso</th>
                                <th className="px-3 py-3 text-center font-semibold border">Nro Cuotas</th>
                                <th className="px-3 py-3 text-center font-semibold border">Fecha Límite</th>
                                <th className="px-3 py-3 text-center font-semibold border">Capacidad</th>
                                <th className="px-3 py-3 text-center font-semibold border">Observaciones</th>
                                <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                                <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {filteredCursos.length === 0 ? (
                                <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500">No hay cursos registrados</td></tr>
                            ) : (
                                filteredCursos.map((c, index) => (
                                    <tr key={c.id_curso} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 text-center border">{index + 1}</td>
                                        <td className="px-3 py-2 border font-medium">{c.nombre || '-'}</td>
                                        <td className="px-3 py-2 border max-w-xs truncate">{c.descripcion || '-'}</td>
                                        <td className="px-3 py-2 border">{c.costo ? `Bs. ${parseFloat(c.costo).toFixed(2)}` : '-'}</td>
                                        <td className="px-3 py-2 border">{c.numero_cuotas || '-'}</td>
                                        <td className="px-3 py-2 border text-center whitespace-nowrap">
                                            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-medium border border-gray-200">
                                                {formatSafeDate(c.fecha_limite_inscripcion)}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 border font-semibold text-blue-700">{c.capacidad || '30'}</td>
                                        <td className="px-3 py-2 border">{c.observaciones || '-'}</td>
                                        <td className="px-3 py-2 border text-center">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(c.estado)}`}>
                                                {getEstadoLabel(c.estado)}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 border">
                                            <div className="flex justify-center gap-2">
                                                <button onClick={() => handleViewModulos(c)} className="p-2 rounded-full transition shadow-sm bg-blue-100 text-blue-600 hover:bg-blue-200" title="Ver Detalles">
                                                    <Eye size={16} />
                                                </button>
                                                <button onClick={() => handleEdit(c)} className="p-2 rounded-full transition shadow-sm bg-yellow-100 text-yellow-600 hover:bg-yellow-200" title="Editar">
                                                    <Edit size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal Crear/Editar Curso */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-blue-900">{editingId ? 'Editar Curso' : 'Añadir Curso'}</h2>
                            <button onClick={() => { setShowModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
                        </div>
                        <div className="p-6 space-y-6">
                            {/* Información del Curso */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Nombre Curso <span className="text-red-500">*</span></label>
                                    <input type="text" name="nombre" value={formData.nombre} onChange={handleInputChange}
                                        placeholder="Ingrese nombre del curso"
                                        className={`w-full px-3 py-2 border-2 rounded-md ${errors.nombre ? 'border-red-500' : 'border-gray-300'}`} />
                                    {errors.nombre && <p className="mt-1 text-sm text-red-600">{errors.nombre}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Descripción Curso</label>
                                    <input type="text" name="descripcion" value={formData.descripcion} onChange={handleInputChange}
                                        placeholder="Descripción del curso"
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Precio Curso <span className="text-red-500">*</span></label>
                                    <input type="number" name="costo" value={formData.costo} onChange={handleInputChange}
                                        placeholder="Ej: 500"
                                        className={`w-full px-3 py-2 border-2 rounded-md ${errors.costo ? 'border-red-500' : 'border-gray-300'}`} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">No. Cuotas <span className="text-red-500">*</span></label>
                                    <select name="numero_cuotas" value={formData.numero_cuotas} onChange={handleInputChange}
                                        className={`w-full px-3 py-2 border-2 rounded-md ${errors.numero_cuotas ? 'border-red-500' : 'border-gray-300'}`}>
                                        <option value="">Seleccionar</option>
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Capacidad <span className="text-blue-500 font-bold">(Cupos)</span></label>
                                    <input type="number" name="capacidad" value={formData.capacidad} onChange={handleInputChange}
                                        placeholder="Ej: 30"
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Observaciones</label>
                                    <input type="text" name="observaciones" value={formData.observaciones} onChange={handleInputChange}
                                        placeholder="Observaciones adicionales"
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md" />
                                </div>
                            </div>

                            {/* Módulos del Curso */}
                            <div className="border-t pt-4">
                                <h3 className="text-lg font-semibold text-blue-700 mb-4">Módulos del Curso</h3>

                                {formData.modulos.map((modulo, index) => (
                                    <div key={index} className="bg-gray-50 rounded-lg p-4 mb-4 relative">
                                        {formData.modulos.length > 1 && (
                                            <button
                                                onClick={() => removeModulo(index)}
                                                className="absolute top-2 right-2 text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
                                            >
                                                🗑 Eliminar módulo
                                            </button>
                                        )}

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Módulo</label>
                                                <input type="text" value={modulo.nombre}
                                                    onChange={(e) => handleModuloChange(index, 'nombre', e.target.value)}
                                                    placeholder="Nombre del módulo"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Facilitador</label>
                                                <input type="text" value={modulo.facilitador}
                                                    onChange={(e) => handleModuloChange(index, 'facilitador', e.target.value)}
                                                    placeholder="campo abierto"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100" />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Fecha Inicio</label>
                                                <input type="date" value={modulo.fecha_inicio}
                                                    onChange={(e) => handleModuloChange(index, 'fecha_inicio', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Fecha Fin</label>
                                                <input type="date" value={modulo.fecha_fin}
                                                    onChange={(e) => handleModuloChange(index, 'fecha_fin', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Hora Inicio</label>
                                                <input type="time" value={modulo.hora_inicio}
                                                    onChange={(e) => handleModuloChange(index, 'hora_inicio', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Hora Fin</label>
                                                <input type="time" value={modulo.hora_fin}
                                                    onChange={(e) => handleModuloChange(index, 'hora_fin', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Días</label>
                                                <div className="flex gap-1 mt-1">
                                                    {[
                                                        { key: 'lunes', label: 'L' },
                                                        { key: 'martes', label: 'M' },
                                                        { key: 'miercoles', label: 'Mi' },
                                                        { key: 'jueves', label: 'J' },
                                                        { key: 'viernes', label: 'V' }
                                                    ].map(dia => (
                                                        <label key={dia.key} className="flex items-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={modulo.dias[dia.key]}
                                                                onChange={(e) => handleModuloChange(index, `dia_${dia.key}`, e.target.checked)}
                                                                className="w-4 h-4 border-2 border-gray-300 rounded"
                                                            />
                                                            <span className="ml-1 text-xs">{dia.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Duración</label>
                                                <input type="text" value={modulo.duracion}
                                                    onChange={(e) => handleModuloChange(index, 'duracion', e.target.value)}
                                                    placeholder="Ej: 20 hrs"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Carga Horaria</label>
                                                <input type="text" value={modulo.carga_horaria}
                                                    onChange={(e) => handleModuloChange(index, 'carga_horaria', e.target.value)}
                                                    placeholder="Ej: 4 hrs/sem"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Sesiones</label>
                                                <input type="text" value={modulo.sesiones}
                                                    onChange={(e) => handleModuloChange(index, 'sesiones', e.target.value)}
                                                    placeholder="Ej: 10"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Sala</label>
                                                <input type="text" value={modulo.sala}
                                                    onChange={(e) => handleModuloChange(index, 'sala', e.target.value)}
                                                    placeholder="Ej: Aula 101"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <button
                                    onClick={addModulo}
                                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium border border-blue-300 px-4 py-2 rounded-md hover:bg-blue-50"
                                >
                                    + Agregar módulo
                                </button>
                            </div>

                            {/* Botones */}
                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button onClick={() => { setShowModal(false); resetForm(); }} className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Cancelar</button>
                                <button onClick={handleSubmit} className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800">Guardar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Ver Módulos */}
            {showModulosModal && selectedCurso && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-blue-900">Detalles del Curso</h2>
                            <button onClick={() => { setShowModulosModal(false); setSelectedCurso(null); }} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6 space-y-6">
                            {/* Información General del Curso */}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-blue-800 mb-4">Información del Curso</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div>
                                        <span className="text-sm text-gray-500">Nombre Curso</span>
                                        <p className="font-medium">{selectedCurso.nombre || '-'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Descripción</span>
                                        <p className="font-medium">{selectedCurso.descripcion || selectedCurso.modalidad || '-'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Precio Curso</span>
                                        <p className="font-medium">Bs. {selectedCurso.costo || '-'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Número de Cuotas</span>
                                        <p className="font-medium">{selectedCurso.numero_cuotas || '-'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500 text-blue-600 font-semibold">Fecha Límite Inscripción</span>
                                        <p className="font-bold text-blue-800">{formatSafeDate(selectedCurso.fecha_limite_inscripcion)}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Observaciones</span>
                                        <p className="font-medium">{selectedCurso.observaciones || '-'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Estado</span>
                                        {/* Agregamos flex, items-center y gap-2 aquí */}
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className={`inline-block px-2 py-1 rounded-full text-xs ${getEstadoColor(selectedCurso.estado)}`}>
                                                {getEstadoLabel(selectedCurso.estado)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Módulos del Curso */}
                            <div>
                                <h3 className="text-lg font-semibold text-blue-800 mb-4">Módulos del Curso</h3>
                                <div className="bg-white rounded-lg shadow overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead className="bg-gray-100 border-b">
                                                <tr>
                                                    <th className="px-3 py-3 text-center font-semibold border">Módulo</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Facilitador</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Fecha Inicio</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Fecha Fin</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Hora Inicio</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Hora Fin</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Días</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Duración</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Carga Horaria</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Sesiones</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Sala</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y">
                                                {selectedCurso.modulos && selectedCurso.modulos.length > 0 ? (
                                                    selectedCurso.modulos.map((modulo, idx) => (
                                                        <tr key={idx} className="hover:bg-gray-50">
                                                            <td className="px-3 py-2 text-center border">{modulo.nombre || `Módulo ${idx + 1}`}</td>
                                                            <td className="px-3 py-2 border">{modulo.facilitador || '-'}</td>
                                                            <td className="px-3 py-2 border">{formatSafeDate(modulo.fecha_inicio)}</td>
                                                            <td className="px-3 py-2 border">{formatSafeDate(modulo.fecha_fin)}</td>
                                                            <td className="px-3 py-2 border">{modulo.hora_inicio || '-'}</td>
                                                            <td className="px-3 py-2 border">{modulo.hora_fin || '-'}</td>
                                                            <td className="px-3 py-2 border text-center">{formatDias(modulo.dias)}</td>
                                                            <td className="px-3 py-2 border">{modulo.duracion || '-'}</td>
                                                            <td className="px-3 py-2 border">{modulo.carga_horaria || '-'}</td>
                                                            <td className="px-3 py-2 border">{modulo.sesiones || '-'}</td>
                                                            <td className="px-3 py-2 border">{modulo.sala || '-'}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="11" className="px-4 py-8 text-center text-gray-500">
                                                            No hay módulos registrados para este curso
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {/* Botón Cerrar */}
                            <div className="flex justify-end pt-4">
                                <button
                                    onClick={() => { setShowModulosModal(false); setSelectedCurso(null); }}
                                    className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

